import { useCallback, useEffect, useRef, useState } from 'react';
import { getStats, getStatsHistory } from '../api/client';

const POLL_INTERVAL_MS = 15000;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await getStats();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted py-5">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        Loading stats…
      </div>
    );
  }

  return (
    <div>
      <h1 className="h4 mb-3">Sending progress</h1>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {stats && (
        <>
          <div className="row g-3 mb-4">
            <StatCard label="Pending total" value={stats.pending_total} cols="col-6 col-md-3" />
            <StatCard label="Exhausted total" value={stats.exhausted_total} highlight cols="col-6 col-md-3" />
            <StatCard
              label="Oldest pending since"
              value={stats.oldest_pending_created_at || '—'}
              small
              cols="col-6 col-md-3"
            />
            <PendingByStatusCard breakdown={stats.pending_by_status} />
          </div>

          <p className="text-muted small mb-4">Last refreshed: {stats.generated_at}</p>
        </>
      )}

      <HistoricalVolume />
    </div>
  );
}

// Success/failed/exhausted counts over a chosen date+time range, backed by
// CallbackStatsStore's Redis hourly-bucketed counters (added 2026-07-20,
// moved from daily to hourly 2026-07-21 specifically so a range can be
// narrower than a whole day) - separate data source from the live-state
// cards above, since a succeeded callback leaves no trace in
// pending_index/done_index to count after the fact. Fetched on demand (on
// mount, and whenever "Apply" is clicked) rather than on the 15s poll loop
// above, since history doesn't change fast enough to need that.
function HistoricalVolume() {
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // datetime-local inputs give a "YYYY-MM-DDTHH:mm" string with no
  // timezone - passed straight through to the API, which parses it with
  // Carbon::parse() against the app's own configured timezone. Left empty
  // by default so the initial load uses the API's own default range
  // (today, midnight to now - changed from a 30-day default 2026-07-23)
  // rather than duplicating that default here.
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');

  const load = useCallback(async (range) => {
    setLoading(true);
    try {
      const data = await getStatsHistory(range || {});
      setHistory(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load historical stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const applyRange = (e) => {
    e.preventDefault();
    load({ from: fromInput || undefined, to: toInput || undefined });
  };

  const resetRange = () => {
    setFromInput('');
    setToInput('');
    load();
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h2 className="h6 text-uppercase text-muted mb-0">
          Historical volume {history ? `(${formatRangeLabel(history)})` : ''}
        </h2>
      </div>

      <form className="row row-cols-lg-auto g-2 align-items-end my-2" onSubmit={applyRange}>
        <div className="col-12">
          <label className="form-label small text-muted mb-1" htmlFor="stats-from">
            From
          </label>
          <input
            id="stats-from"
            type="datetime-local"
            className="form-control form-control-sm"
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
          />
        </div>
        <div className="col-12">
          <label className="form-label small text-muted mb-1" htmlFor="stats-to">
            To
          </label>
          <input
            id="stats-to"
            type="datetime-local"
            className="form-control form-control-sm"
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
          />
        </div>
        <div className="col-12 d-flex gap-2">
          <button type="submit" className="btn btn-sm btn-primary" disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetRange} disabled={loading}>
            Reset to today
          </button>
        </div>
      </form>
      <p className="text-muted small mb-2">
        A range of 3 days or less shows hourly detail; wider ranges are rolled up into daily
        totals so the chart stays readable.
      </p>

      {error && <div className="alert alert-danger py-2 mt-2">{error}</div>}

      {history && (
        <>
          <div className="row g-3 my-1">
            <StatCard label="Succeeded" value={history.totals.success} />
            <StatCard label="Failed (retried)" value={history.totals.failed} />
            <StatCard label="Exhausted" value={history.totals.exhausted} highlight />
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <HistoryLineChart buckets={history.buckets} granularity={history.granularity} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatRangeLabel(history) {
  const from = history.from ? history.from.replace('T', ' ').slice(0, 16) : '';
  const to = history.to ? history.to.replace('T', ' ').slice(0, 16) : '';
  return `${from} to ${to}, ${history.granularity === 'hour' ? 'hourly' : 'daily'}`;
}

// Deliberately no charting library dependency (recharts/chart.js/etc.) -
// this is a small, fixed-shape multi-series chart, and adding a whole
// library for it wasn't judged worth the extra bundle weight / install
// surface. Plain inline SVG, scaled to whichever bucket has the highest
// single-outcome count in the current range.
const SERIES = [
  { key: 'success', label: 'Succeeded', color: '#198754' },
  { key: 'failed', label: 'Failed', color: '#fd7e14' },
  { key: 'exhausted', label: 'Exhausted', color: '#dc3545' },
];

// Switched from grouped bars to lines (2026-07-27), per direct feedback that
// the bar chart's bottom-axis date/time labels were unreadable - most ranges
// have long stretches of zero-value buckets (a healthy pipeline drains
// pending callbacks fast), so a bar chart was mostly empty space punctuated
// by a few tall bars, and cramming a rotated 9px label under every ~10th
// narrow bar group left them overlapping and illegible. A line connects the
// same data points without needing per-bucket bar width, freeing up real
// horizontal room to space labels out - see labelStep below, which picks
// however few labels actually fit without overlapping rather than a fixed
// "every 10th" rule.
//
// Renamed from HistoryBarChart. Still handles either granularity the API
// can return - 'hour' buckets (label like "2026-07-20T14") for a narrow
// range, or 'day' buckets (label like "2026-07-20") for a wide one - only
// the axis-label formatting differs between the two.
//
// 2026-07-27 follow-up: added a Y-axis scale (there was previously no way to
// read magnitude without hovering) and a custom hover tooltip (the native
// SVG <title> tooltip it had before was slow to appear and only showed one
// series at a time) - hovering anywhere along a column now shows the full
// bucket label plus all three series' values at once.
function HistoryLineChart({ buckets, granularity }) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Measures the actual rendered width of the scroll wrapper below, so the
  // chart can stretch to fill it (2026-07-27 - previously a fixed 40px
  // per-point spacing left a short range like "today" looking stranded in
  // the left third of a wide card, with empty space to the right).
  // ResizeObserver (not just a mount-time measurement) so this also
  // reacts to the browser window resizing or the sidebar/layout changing.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (width) {
        setContainerWidth(width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!buckets || buckets.length === 0) {
    return <p className="text-muted small mb-0">No data in this range yet.</p>;
  }

  const maxValue = Math.max(1, ...buckets.flatMap((b) => SERIES.map((s) => b[s.key] || 0)));

  const topPad = 10;
  const chartHeight = 160;
  const bottomLabelHeight = 30;
  const yAxisWidth = 56;
  const rightPad = 16;
  const plotLeft = yAxisWidth;

  // Fill the available container width when there's room to (few buckets -
  // stretch point spacing out to fill it), but never compress below a
  // comfortable minimum (many buckets - keep the old fixed-spacing/scroll
  // behavior instead of cramming points together illegibly). Whichever of
  // "natural" width (buckets at the minimum spacing) or the measured
  // container width is larger wins.
  const minPointSpacing = 40;
  const naturalWidth = plotLeft + rightPad + (buckets.length - 1) * minPointSpacing;
  const chartWidth = Math.max(320, naturalWidth, containerWidth);
  const pointSpacing = buckets.length > 1
    ? (chartWidth - plotLeft - rightPad) / (buckets.length - 1)
    : minPointSpacing;
  const svgHeight = topPad + chartHeight + bottomLabelHeight;

  const fullLabel = (label) => {
    // Unlike axisLabel() below (which abbreviates for the tick marks), the
    // hover tooltip has room to show the full date/time - useful since
    // only a subset of buckets get an axis tick at all (see labelStep
    // below).
    if (granularity === 'hour') {
      const [datePart, hourPart] = label.split('T');
      return `${datePart} ${hourPart}:00`;
    }
    // Day buckets are a whole-day sum, not a single instant - showing the
    // covered range (rather than a bare date, or a misleading single
    // "00:00") makes clear what window of time the point represents.
    return `${label} (00:00–23:59)`;
  };

  // True when every bucket falls on the same calendar day (e.g. the "today"
  // range) - in that case the date part of an hour label is redundant on
  // every single tick, so the axis only needs to show the time.
  const bucketDate = (label) => (granularity === 'hour' ? label.split('T')[0] : label);
  const isSingleDay = buckets.every((b) => bucketDate(b.label) === bucketDate(buckets[0].label));

  const axisLabel = (label) => {
    // Hour labels are "YYYY-MM-DDTHH" - show just "HH:00" when the whole
    // range is one day (the date would just repeat on every tick), or
    // "MM-DD HH:00" once the range spans more than a day so ticks stay
    // unambiguous across the boundary. Day labels are "YYYY-MM-DD" - show
    // "MM-DD" to match the original daily chart.
    if (granularity === 'hour') {
      const [datePart, hourPart] = label.split('T');
      return isSingleDay ? `${hourPart}:00` : `${datePart.slice(5)} ${hourPart}:00`;
    }
    return label.slice(5);
  };

  const x = (i) => plotLeft + i * pointSpacing;
  const y = (value) => topPad + chartHeight - (value / maxValue) * chartHeight;

  // Only show as many labels as actually fit horizontally without
  // overlapping - each label needs roughly 65px of its own space once
  // rendered upright (no rotation, unlike the old bar chart), so this picks
  // the largest step that keeps consecutive shown labels at least that far
  // apart, instead of a fixed "every Nth bucket" rule that didn't account
  // for how wide the chart actually ended up.
  const minLabelGapPx = 65;
  const maxLabelCount = Math.max(2, Math.floor((chartWidth - plotLeft) / minLabelGapPx));
  const labelStep = Math.max(1, Math.ceil(buckets.length / maxLabelCount));

  // 4 evenly-spaced Y-axis gridlines (0%, 33%, 66%, 100% of maxValue) - just
  // enough to read approximate magnitude at a glance, without cluttering a
  // chart this small with too many ticks.
  const yTicks = [0, 1 / 3, 2 / 3, 1].map((fraction) => Math.round(fraction * maxValue));

  const hoverBucket = hoverIndex !== null ? buckets[hoverIndex] : null;

  // Tooltip box sized/positioned from the hovered point - clamped so it
  // never runs off either edge of the chart.
  // Wide enough for the longest label this can show - the day-granularity
  // "(00:00–23:59)" range suffix added 2026-07-27 is the longest case.
  const tooltipWidth = 190;
  const tooltipHeight = 20 + SERIES.length * 16;
  const tooltipX = hoverIndex !== null
    ? Math.min(Math.max(x(hoverIndex) - tooltipWidth / 2, plotLeft), chartWidth - tooltipWidth)
    : 0;
  const tooltipY = 2;

  return (
    <div>
      <div className="d-flex gap-3 mb-2 small text-muted">
        {SERIES.map((s) => (
          <span key={s.key} className="d-flex align-items-center gap-1">
            <span
              style={{ display: 'inline-block', width: 10, height: 10, background: s.color, borderRadius: 2 }}
            />
            {s.label}
          </span>
        ))}
      </div>

      <div ref={containerRef} style={{ overflowX: 'auto' }}>
        <svg
          width={chartWidth}
          height={svgHeight}
          role="img"
          aria-label={`Callback outcomes by ${granularity}`}
        >
          {/* Y-axis gridlines + value labels */}
          {yTicks.map((tickValue) => (
            <g key={tickValue}>
              <line
                x1={plotLeft}
                y1={y(tickValue)}
                x2={chartWidth}
                y2={y(tickValue)}
                stroke="#f1f3f5"
                strokeWidth={1}
              />
              <text x={plotLeft - 8} y={y(tickValue) + 3} fontSize="10" fill="#6c757d" textAnchor="end">
                {tickValue.toLocaleString()}
              </text>
            </g>
          ))}

          {/* Baseline axis */}
          <line
            x1={plotLeft}
            y1={topPad + chartHeight}
            x2={chartWidth}
            y2={topPad + chartHeight}
            stroke="#dee2e6"
            strokeWidth={1}
          />

          {SERIES.map((s) => {
            const points = buckets.map((b, i) => `${x(i)},${y(b[s.key] || 0)}`).join(' ');
            return <polyline key={s.key} points={points} fill="none" stroke={s.color} strokeWidth={2} />;
          })}

          {buckets.map((bucket, i) => (
            <g key={bucket.label}>
              {SERIES.map((s) => {
                const value = bucket[s.key] || 0;
                return (
                  <circle
                    key={s.key}
                    cx={x(i)}
                    cy={y(value)}
                    r={hoverIndex === i ? 4 : 2.5}
                    fill={s.color}
                  />
                );
              })}
              {i % labelStep === 0 && (
                <text x={x(i)} y={topPad + chartHeight + 20} fontSize="10" fill="#6c757d" textAnchor="middle">
                  {axisLabel(bucket.label)}
                </text>
              )}
              {/* Invisible hit area spanning this column's full height -
                  much easier to hover than the small point markers alone. */}
              <rect
                x={x(i) - pointSpacing / 2}
                y={topPad}
                width={pointSpacing}
                height={chartHeight}
                fill="transparent"
                onMouseEnter={() => setHoverIndex(i)}
                onMouseLeave={() => setHoverIndex((current) => (current === i ? null : current))}
              />
            </g>
          ))}

          {hoverIndex !== null && (
            <line
              x1={x(hoverIndex)}
              y1={topPad}
              x2={x(hoverIndex)}
              y2={topPad + chartHeight}
              stroke="#adb5bd"
              strokeWidth={1}
              strokeDasharray="3,3"
              pointerEvents="none"
            />
          )}

          {hoverBucket && (
            <g pointerEvents="none">
              <rect
                x={tooltipX}
                y={tooltipY}
                width={tooltipWidth}
                height={tooltipHeight}
                rx={4}
                fill="#212529"
                opacity={0.92}
              />
              <text x={tooltipX + 8} y={tooltipY + 16} fontSize="10" fill="#f8f9fa" fontWeight="bold">
                {fullLabel(hoverBucket.label)}
              </text>
              {SERIES.map((s, idx) => (
                <text
                  key={s.key}
                  x={tooltipX + 8}
                  y={tooltipY + 32 + idx * 16}
                  fontSize="10"
                  fill="#f8f9fa"
                >
                  {`${s.label}: ${(hoverBucket[s.key] || 0).toLocaleString()}`}
                </text>
              ))}
            </g>
          )}
        </svg>
      </div>
    </div>
  );
}

// (2026-08-06 follow-up) StatCard now takes an optional `cols` prop
// (default unchanged, col-12 col-md-4 - 3-per-row, still used as-is by
// HistoricalVolume's Succeeded/Failed/Exhausted row below) so the "Pending
// by status" breakdown could be folded directly into the SAME row as
// Pending total/Exhausted total/Oldest pending since (col-6 col-md-3 - 4
// per row) instead of a visually separate section underneath it - one
// continuous card grid rather than two stacked groups.
function StatCard({ label, value, highlight, small, cols = 'col-12 col-md-4' }) {
  return (
    <div className={cols}>
      <div className={`card h-100 border-0 shadow-sm${highlight ? ' border-start border-warning border-4' : ''}`}>
        <div className="card-body">
          <div className="text-muted small">{label}</div>
          <div className={small ? 'fs-6 fw-medium mt-1' : 'fs-3 fw-bold mt-1'}>{value}</div>
        </div>
      </div>
    </div>
  );
}

// (2026-08-06, second follow-up) The "Pending by status" breakdown as its
// own 4th column card, alongside Pending total/Exhausted total/Oldest
// pending since - not one card per status (the previous attempt's 7-card
// grid) but ONE card whose body lists all 4 statuses, sized the same
// col-6 col-md-3 as its 3 siblings so the row reads as a clean 4-column
// grid on desktop.
function PendingByStatusCard({ breakdown }) {
  const entries = Object.entries(breakdown || {});
  return (
    <div className="col-6 col-md-3">
      <div className="card h-100 border-0 shadow-sm">
        <div className="card-body">
          <div className="text-muted small mb-2">Pending by status</div>
          <div className="d-flex flex-column">
            {entries.map(([status, count], idx) => (
              <div
                key={status}
                className={`d-flex align-items-center justify-content-between py-1${
                  idx < entries.length - 1 ? ' border-bottom' : ''
                }`}
              >
                <span className="text-capitalize small">{status}</span>
                <span className="fw-semibold">{count}</span>
              </div>
            ))}
            {entries.length === 0 && <span className="text-muted small">No data.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
